import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";

import { fetchClubEmployees } from "../api/clubApi";
import {
  asClubEmployeesList,
  pickClubEmployeeEmpref,
  pickClubEmployeeUserId,
} from "../helpers/clubEmployeeList";
import { readStoredAccessToken } from "../helpers/authToken";
import Constant from "../util/constant";
import useAuth from "./useAuth";

function normalizeEmpref(value) {
  const trimmed = value != null ? String(value).trim() : "";
  return trimmed ? trimmed.toUpperCase() : "";
}

function readStoredEmpref() {
  try {
    const raw = window.localStorage.getItem(Constant.LOCALSTORAGEKEYS.EMPREF);
    if (raw != null && raw !== "" && raw !== "undefined") {
      return normalizeEmpref(raw);
    }
  } catch {
    /* ignore */
  }
  return "";
}

function pickEmprefFromJwtPayload(decoded) {
  if (!decoded || typeof decoded !== "object") return "";
  const direct = decoded.empref ?? decoded.empRef ?? decoded.emp_ref;
  if (direct != null && String(direct).trim() !== "") {
    return normalizeEmpref(direct);
  }
  const nestedUser = decoded.user;
  if (nestedUser && typeof nestedUser === "object") {
    const fromUser =
      nestedUser.empref ?? nestedUser.empRef ?? nestedUser.emp_ref;
    if (fromUser != null && String(fromUser).trim() !== "") {
      return normalizeEmpref(fromUser);
    }
  }
  return "";
}

function pickEmprefFromToken() {
  try {
    const token = readStoredAccessToken();
    if (!token) return "";
    return pickEmprefFromJwtPayload(jwtDecode(token));
  } catch {
    return "";
  }
}

function rememberEmpref(value) {
  const normalized = normalizeEmpref(value);
  if (normalized) {
    window.localStorage.setItem(Constant.LOCALSTORAGEKEYS.EMPREF, normalized);
  }
  return normalized;
}

/**
 * Resolves the logged-in club employee's staff reference code.
 * Order: localStorage (login) → JWT claims → GET /club/employee/club/:clubId/employees.
 */
export default function useMyEmpref() {
  const { managedClubId, userId, isClubEmployee } = useAuth();
  const [empref, setEmpref] = useState(() => {
    return readStoredEmpref() || pickEmprefFromToken();
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fromStore = readStoredEmpref();
    const fromJwt = pickEmprefFromToken();
    const immediate = fromStore || fromJwt;
    if (immediate) {
      setEmpref(immediate);
      if (!fromStore && fromJwt) rememberEmpref(fromJwt);
      return undefined;
    }

    if (!isClubEmployee || !managedClubId || !userId) {
      setEmpref("");
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchClubEmployees(managedClubId)
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data ?? res;
        let list = asClubEmployeesList(raw);
        if (
          list.length === 0 &&
          raw &&
          typeof raw === "object" &&
          raw.data != null
        ) {
          list = asClubEmployeesList(raw.data);
        }
        const uid = String(userId);
        const hit = list.find(
          (row) => String(pickClubEmployeeUserId(row)) === uid
        );
        const resolved = hit ? pickClubEmployeeEmpref(hit) : "";
        const stored = resolved ? rememberEmpref(resolved) : "";
        setEmpref(stored);
      })
      .catch(() => {
        if (!cancelled) setEmpref("");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isClubEmployee, managedClubId, userId]);

  return { empref, loading };
}
